// src/data/topUpSoRules.ts
// ------------------------
// Repository for `top_up_so_rules` (db-159) — the editable per-section Stock-Top-up
// routing rules (line_mode + include_discount) the Phase-4 SO builder reads and the
// Top-up SO Rules admin screen edits. Pairs with top_up_product_map (product code +
// suffix). Typed via getTypedClient().

import type { Database } from "../types/database.generated";
import { logger } from "../utils/logger";
import { getTypedClient } from "../utils/supabase";

export interface TopUpSoRuleRow {
  id: string;
  section: string;
  variant: string;
  line_mode: string;
  include_discount: boolean;
  suffix: string;
  sort_order: number;
  deactivated: boolean;
}

// Typed as `string` so supabase-js doesn't deep-parse the select.
const TOP_UP_SO_RULE_COLS: string =
  "id, section, variant, line_mode, include_discount, suffix, sort_order, deactivated";

// The rules, sort_order-ordered — active only by default (what the builder uses),
// pass includeDeactivated for the admin view. [] on error / no client.
export async function listTopUpSoRules(includeDeactivated = false): Promise<TopUpSoRuleRow[]> {
  const c = getTypedClient();
  if (!c) return [];
  let q = c.from("top_up_so_rules").select(TOP_UP_SO_RULE_COLS);
  if (!includeDeactivated) q = q.eq("deactivated", false);
  const { data, error } = await q.order("sort_order").limit(200);
  if (error) {
    logger.warn("listTopUpSoRules failed:", error.message);
    return [];
  }
  return (data ?? []) as unknown as TopUpSoRuleRow[];
}

type TopUpSoRulePatch = Database["public"]["Tables"]["top_up_so_rules"]["Update"];
type WriteResult = { error: { message: string } | null };

// Update a rule by id (the admin editor edits line_mode / include_discount / active).
// Takes a loose patch cast to the generated Update shape, per the repo write convention.
export async function updateTopUpSoRuleById(
  id: string | undefined,
  patch: object
): Promise<WriteResult> {
  const c = getTypedClient();
  if (!c) return { error: null };
  const { error } = await c
    .from("top_up_so_rules")
    .update(patch as TopUpSoRulePatch)
    .eq("id", id ?? "");
  if (error) logger.warn("updateTopUpSoRuleById failed:", error.message);
  return { error };
}
